import { useState, useCallback, useRef } from "react";
import type { FormDialogProps } from "./FormDialog";

export interface UseFormDialogOptions {
  /** Async submit action. Throw to keep the dialog open and surface an error. */
  onSubmit: () => Promise<void>;
  /** Called when the dialog closes — on both successful submit and explicit cancel. */
  onClose?: () => void;
}

export interface UseFormDialogResult {
  loading: boolean;
  error: string | null;
  /** Spread onto <FormDialog> alongside open, title, children, and any extra props. */
  dialogProps: Pick<FormDialogProps, "onClose" | "onSubmit" | "loading" | "error">;
}

/**
 * Wraps a form submit action with loading/error lifecycle.
 * On success: calls onClose. On failure: surfaces the error and keeps the dialog open.
 * Prevents close during an in-flight submit.
 */
export function useFormDialog({ onSubmit, onClose }: UseFormDialogOptions): UseFormDialogResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await onSubmitRef.current();
      setError(null);
      onCloseRef.current?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleClose = useCallback(() => {
    if (loading) return;
    setError(null);
    onCloseRef.current?.();
  }, [loading]);

  return {
    loading,
    error,
    dialogProps: { onClose: handleClose, onSubmit: handleSubmit, loading, error },
  };
}
